import { useState } from "react";
import { useApi, apiPatch, apiPost, apiDelete } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { PageHeader } from "@/components/common/PageHeader";
import { LoadingBlock, ErrorState } from "@/components/common/StateViews";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Lock, Trash2, Save, ShieldCheck } from "lucide-react";

const MODULES = ["Lens", "Guard", "Ledger", "Gate", "Hub", "Core", "Companion"];
const ACTIONS = [["view", "View"], ["configure", "Configure"], ["approve", "Approve"]];
const APP_CATALOG = [
  ["cockpit", "Cockpit"], ["risk", "Risk"], ["ledger", "Cost Ledger"], ["savings", "Savings Autopilot"],
  ["usage", "Usage & Value"], ["impact", "Impact Analytics"], ["gateway", "Gateway"], ["policies", "Policies"],
  ["apps", "AI Apps Inventory"], ["hub", "Integration Hub"], ["evidence", "Evidence"], ["access", "Access (RBAC)"],
  ["settings", "Tenant Settings"], ["help", "Help & Guides"],
  ["home", "Companion Home"], ["tools", "My AI Tools"], ["prompt", "Prompt Studio"], ["advisor", "Advisor"],
  ["activity", "My Activity"], ["policy", "Policy in Plain Words"], ["requests", "Requests"], ["learn", "Learn"],
];

export default function Access() {
  const { data: roles = [], isLoading, error, refetch } = useApi("roles", "/roles");
  const { role } = useApp();
  const [selKey, setSelKey] = useState(null);
  const [draft, setDraft] = useState(null);
  const [creating, setCreating] = useState(false);
  const [newRole, setNewRole] = useState({ name: "", purpose: "" });

  const sel = draft || roles.find((r) => r.key === selKey) || roles[0];
  const pick = (r) => { setSelKey(r.key); setDraft(JSON.parse(JSON.stringify(r))); };
  const togglePerm = (mod, act) => setDraft((d) => ({ ...d, permissions: { ...d.permissions, [mod]: { ...d.permissions[mod], [act]: !d.permissions[mod]?.[act] } } }));
  const toggleApp = (k) => setDraft((d) => { const has = d.apps.includes(k); return { ...d, apps: has ? d.apps.filter((x) => x !== k) : [...d.apps, k] }; });

  const save = async () => {
    try { await apiPatch(`/roles/${draft.key}`, { name: draft.name, apps: draft.apps, permissions: draft.permissions, purpose: draft.purpose });
      toast.success(`Role "${draft.name}" updated`); refetch(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
  };
  const removeRole = async () => { try { await apiDelete(`/roles/${draft.key}`); toast.success("Role deleted"); setDraft(null); setSelKey(null); refetch(); } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); } };
  const createRole = async () => {
    if (!newRole.name.trim()) return toast.error("Role name required");
    try { const r = await apiPost("/roles", { name: newRole.name, purpose: newRole.purpose, apps: ["cockpit", "help"] });
      toast.success(`Role "${r.name}" created`); setCreating(false); setNewRole({ name: "", purpose: "" }); await refetch(); pick(r); }
    catch { toast.error("Create failed"); }
  };

  if (error) return <ErrorState />;
  if (isLoading) return <LoadingBlock />;
  const active = sel || roles[0];

  return (
    <div data-testid="access-rbac">
      <PageHeader eyebrow="Access Control" title="Access (RBAC)" subtitle={`${roles.length} roles. Define roles, edit the permission matrix and control which apps each role can open.`}>
        <Button size="sm" onClick={() => setCreating(true)} data-testid="new-role"><Plus className="h-4 w-4 mr-1.5" />New role</Button>
      </PageHeader>

      <div className="grid lg:grid-cols-[300px_1fr] gap-4">
        {/* Role list */}
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base">Roles</CardTitle></CardHeader>
          <CardContent className="p-2 max-h-[62vh] overflow-y-auto space-y-1">
            {roles.map((r) => (
              <button key={r.key} onClick={() => pick(r)} data-testid={`role-row-${r.key}`}
                className={"w-full text-left rounded-lg px-3 py-2 transition-colors " + (active?.key === r.key ? "bg-navy text-white" : "hover:bg-muted")}>
                <div className="flex items-center gap-1.5"><span className="text-sm font-medium">{r.name}</span>
                  {r.locked && <Lock className={"h-3 w-3 " + (active?.key === r.key ? "text-white/70" : "text-muted-foreground")} />}
                  {r.custom && <Badge className="bg-gold text-nav-text text-[9px] px-1 py-0">custom</Badge>}
                  {r.key === role.key && <Badge className={"text-[9px] px-1 py-0 " + (active?.key === r.key ? "bg-white/25" : "bg-gold text-nav-text")}>You</Badge>}
                </div>
                <p className={"text-[11px] leading-tight mt-0.5 " + (active?.key === r.key ? "text-white/70" : "text-muted-foreground")}>{r.apps?.length || 0} apps · {r.scope}</p>
              </button>
            ))}
          </CardContent>
        </Card>

        {/* Editor */}
        {active && (
          <Card>
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between gap-2">
                <CardTitle className="text-base flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-navy" />{active.name}{active.locked && <Badge variant="outline"><Lock className="h-3 w-3 mr-1" />Locked</Badge>}</CardTitle>
                <div className="flex gap-2">
                  {active.custom && !active.locked && <Button size="sm" variant="ghost" className="text-sev-danger" onClick={removeRole} data-testid="delete-role"><Trash2 className="h-3.5 w-3.5" /></Button>}
                  <Button size="sm" onClick={save} disabled={active.locked} data-testid="save-role"><Save className="h-3.5 w-3.5 mr-1.5" />Save</Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-5 max-h-[58vh] overflow-y-auto">
              <div className="space-y-1"><Label className="text-xs">Purpose</Label>
                <Input value={active.purpose || ""} disabled={active.locked} onChange={(e) => setDraft((d) => ({ ...d, purpose: e.target.value }))} data-testid="role-purpose" /></div>

              {/* Permission matrix */}
              <div>
                <p className="text-[11px] uppercase tracking-wide text-muted-foreground mb-2">Permission matrix</p>
                <div className="rounded-lg border overflow-hidden">
                  <div className="grid grid-cols-[1fr_repeat(3,72px)] bg-muted/60 text-[11px] font-medium px-3 py-1.5"><span>Module</span>{ACTIONS.map(([, l]) => <span key={l} className="text-center">{l}</span>)}</div>
                  {MODULES.map((mod) => (
                    <div key={mod} className="grid grid-cols-[1fr_repeat(3,72px)] items-center px-3 py-1.5 border-t text-sm" data-testid={`perm-row-${mod}`}>
                      <span>{mod}</span>
                      {ACTIONS.map(([act]) => (
                        <div key={act} className="flex justify-center">
                          <Switch checked={!!active.permissions?.[mod]?.[act]} disabled={active.locked} onCheckedChange={() => togglePerm(mod, act)} data-testid={`perm-${mod}-${act}`} />
                        </div>
                      ))}
                    </div>
                  ))}
                </div>
              </div>

              {/* App access */}
              <div>
                <p className="text-[11px] uppercase tracking-wide text-muted-foreground mb-2">App access ({active.apps?.length || 0} enabled)</p>
                <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-2">
                  {APP_CATALOG.map(([k, label]) => (
                    <label key={k} className={"flex items-center gap-2 rounded-lg border px-3 py-2 text-sm cursor-pointer " + (active.apps?.includes(k) ? "border-navy bg-navy/5" : "border-border") + (active.locked ? " opacity-60 pointer-events-none" : "")}>
                      <Checkbox checked={active.apps?.includes(k)} onCheckedChange={() => toggleApp(k)} data-testid={`app-${k}`} />{label}
                    </label>
                  ))}
                </div>
              </div>
            </CardContent>
          </Card>
        )}
      </div>

      <Dialog open={creating} onOpenChange={setCreating}>
        <DialogContent data-testid="new-role-dialog">
          <DialogHeader><DialogTitle className="font-display">Create a new role</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1"><Label className="text-xs">Role name</Label><Input value={newRole.name} onChange={(e) => setNewRole({ ...newRole, name: e.target.value })} placeholder="e.g. BU Analyst" data-testid="new-role-name" /></div>
            <div className="space-y-1"><Label className="text-xs">Purpose</Label><Input value={newRole.purpose} onChange={(e) => setNewRole({ ...newRole, purpose: e.target.value })} placeholder="What this role can do" data-testid="new-role-purpose" /></div>
            <p className="text-[11px] text-muted-foreground">Starts with Cockpit + Help access. Configure the full matrix and app access after creating.</p>
          </div>
          <DialogFooter><Button onClick={createRole} data-testid="create-role-btn">Create role</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
